<template>
  <section class="analytics-section" v-if="canLoadWidgets" :key="'carrier-billing-charge-analytics'">
    <section class="mb-3">
      <div>
        <b-row class="align-items-center">
          <b-col cols="12" md="auto" class="mb-2 mb-lg-0">
            <h3>Analytics - Carrier Billing Stats</h3>
            <p class="text-muted mb-0">
              Analyze carrier billing charges by description, carrier, service, and fuel surcharges
            </p>
          </b-col>
          <b-col cols="12" md="5" class="mb-2 mb-lg-0">
            <ActiveFiltersCard
              :items-filters="filters"
              :load-items="applyFilters"
              :reset-filter-with-key="resetFilterWithKey"
            />
          </b-col>
          <b-col cols="12" md="auto" class="text-lg-right ml-auto">
            <b-button
              variant="primary"
              @click="showFilterAside = true"
            >
              <i class="fa fa-filter"></i> Filters
            </b-button>
          </b-col>
        </b-row>
      </div>

      <!-- Filters Aside -->
      <b-sidebar
        v-model="showFilterAside"
        title="Filters"
        right
        shadow
        backdrop
      >
        <div class="px-3 py-2">
          <b-form-group label="For Client:" label-for="filter-brand-id">
            <b-v-select
              id="filter-brand-id"
              v-model="filterForm.brandId"
              :searchable="true"
              :options="brandsFilter"
            />
          </b-form-group>

          <b-form-group label="From Warehouse:" label-for="filter-warehouse-id" class="mt-3">
            <b-v-select
              id="filter-warehouse-id"
              v-model="filterForm.warehouseId"
              :searchable="true"
              :options="warehousesFilter"
            />
          </b-form-group>

          <b-form-group label="Charge Description (contains):" label-for="filter-charge-description" class="mt-3">
            <b-form-input
              id="filter-charge-description"
              v-model="filterForm.chargeDescription"
              placeholder="Search by charge description..."
            ></b-form-input>
          </b-form-group>

          <b-form-group label="From Date" label-for="filter-from-date" class="mt-3">
            <b-form-datepicker
              id="filter-from-date"
              v-model="filterForm.fromDate"
              :max="filterForm.toDate"
            ></b-form-datepicker>
          </b-form-group>

          <b-form-group label="To Date" label-for="filter-to-date" class="mt-3">
            <b-form-datepicker
              id="filter-to-date"
              v-model="filterForm.toDate"
              :min="filterForm.fromDate"
            ></b-form-datepicker>
          </b-form-group>

          <div class="mt-4">
            <b-button
              variant="primary"
              block
              @click="applyFilters"
            >
              Apply Filters
            </b-button>
          </div>
        </div>
      </b-sidebar>

      <div class="animated fadeIn pt-3">
        <b-tabs content-class="mt-0 p-0">

          <b-tab title="By Carrier">
            <b-row>
              <b-col sm="12">
                <ChartCarrierBillingChargeStats
                  group-by="bycarrier"
                  :brand-id="filters.brandId"
                  :warehouse-id="filters.warehouseId"
                  :charge-description="filters.chargeDescription"
                  :show-widget-filters="false"
                  :filter-from-date="filters.fromDate"
                  :filter-to-date="filters.toDate"
                ></ChartCarrierBillingChargeStats>
              </b-col>
            </b-row>
          </b-tab>

          <b-tab title="By Service">
            <b-row>
              <b-col sm="12">
                <ChartCarrierBillingChargeStats
                  group-by="byservice"
                  :brand-id="filters.brandId"
                  :warehouse-id="filters.warehouseId"
                  :charge-description="filters.chargeDescription"
                  :show-widget-filters="false"
                  :filter-from-date="filters.fromDate"
                  :filter-to-date="filters.toDate"
                ></ChartCarrierBillingChargeStats>
              </b-col>
            </b-row>
          </b-tab>
          
          <b-tab title="By Charge Description" active>
            <b-row>
              <b-col sm="12">
                <ChartCarrierBillingChargeStats
                  group-by="bychargedescription"
                  :brand-id="filters.brandId"
                  :warehouse-id="filters.warehouseId"
                  :charge-description="filters.chargeDescription"
                  :show-widget-filters="false"
                  :filter-from-date="filters.fromDate"
                  :filter-to-date="filters.toDate"
                ></ChartCarrierBillingChargeStats>
              </b-col>
            </b-row>
          </b-tab>

          <b-tab title="By Fuel Surcharge">
            <b-row>
              <b-col sm="12">
                <ChartCarrierBillingChargeStats
                  group-by="byfuel"
                  :brand-id="filters.brandId"
                  :warehouse-id="filters.warehouseId"
                  :charge-description="filters.chargeDescription"
                  :show-widget-filters="false"
                  :filter-from-date="filters.fromDate"
                  :filter-to-date="filters.toDate"
                ></ChartCarrierBillingChargeStats>
              </b-col>
            </b-row>
          </b-tab>

          <b-tab title="By Client">
            <b-row>
              <b-col sm="12">
                <ChartCarrierBillingChargeStats
                  group-by="byclient"
                  :brand-id="filters.brandId"
                  :warehouse-id="filters.warehouseId"
                  :charge-description="filters.chargeDescription"
                  :show-widget-filters="false"
                  :filter-from-date="filters.fromDate"
                  :filter-to-date="filters.toDate"
                ></ChartCarrierBillingChargeStats>
              </b-col>
            </b-row>
          </b-tab>

          <b-tab title="By Charge Type">
            <b-row>
              <b-col sm="12">
                <ChartCarrierBillingChargeStats
                  group-by="bychargetype"
                  :brand-id="filters.brandId"
                  :warehouse-id="filters.warehouseId"
                  :charge-description="filters.chargeDescription"
                  :show-widget-filters="false"
                  :filter-from-date="filters.fromDate"
                  :filter-to-date="filters.toDate"
                ></ChartCarrierBillingChargeStats>
              </b-col>
            </b-row>
          </b-tab>
        </b-tabs>
      </div>
    </section>
  </section>
</template>

<script>
import { myCommonDataMixin } from '@/helpers/utils/vueMixins.js';
import ChartCarrierBillingChargeStats from '@/views/components/partials/analytics/ChartCarrierBillingChargeStats.vue';
import ActiveFiltersCard from '@/views/components/ActiveFiltersCard.vue';

export default {
  name: 'CarrierBillingChargeStats',
  components: {
    ChartCarrierBillingChargeStats,
    ActiveFiltersCard
  },
  mixins: [myCommonDataMixin],
  data: function() {
    var todaysDate = new Date();
    var lastQuarterFromDate = new Date();
    lastQuarterFromDate.setMonth(todaysDate.getMonth() - 3);

    return {
      canLoadWidgets: false,
      showFilterAside: false,
      filterForm: {
        brandId: '',
        warehouseId: '',
        chargeDescription: '',
        fromDate: lastQuarterFromDate.toISOString().split('T')[0],
        toDate: todaysDate.toISOString().split('T')[0]
      },
      filters: {
        brandId: '',
        warehouseId: '',
        chargeDescription: '',
        fromDate: lastQuarterFromDate.toISOString().split('T')[0],
        toDate: todaysDate.toISOString().split('T')[0]
      }
    };
  },
  mounted() {
    this.canLoadWidgets = true;
  },
  methods: {
    applyFilters: function() {
      this.filters = Object.assign({}, this.filterForm);
      this.showFilterAside = false;
    },
    resetFilterWithKey: function(key) {
      this.filterForm[key] = '';
      this.filters[key] = '';
    }
  }
};
</script>
